/**
 * The route catalog from spec section 6 (Information Architecture).
 * Copy lives here, separated from logic, so later localisation does not
 * require navigation rewrites (spec section 17.4).
 */
export const SECTIONS = [
  {
    id: 'dashboard',
    label: 'Home',
    description: 'Active grows, grow spaces, and useful next actions',
    group: 'home',
  },
  {
    id: 'spaces',
    label: 'Spaces',
    description: 'Grow spaces and the grows running inside them',
    group: 'grow',
  },
  {
    id: 'scenarios',
    label: 'Scenarios',
    description: 'Build and compare extraction scenarios side by side',
    group: 'extract',
  },
  {
    id: 'runs',
    label: 'Runs',
    description: 'Planned, completed, and archived extraction runs',
    group: 'extract',
  },
  {
    id: 'cultivars',
    label: 'Cultivars',
    description: 'Cultivars, harvest batches, and their linked evidence',
    group: 'grow',
  },
  {
    id: 'equipment',
    label: 'Equipment',
    description: 'Equipment catalog and what it is assigned to',
    group: 'grow',
  },
  {
    id: 'metrics',
    label: 'Metrics',
    description: 'Define and log your own measurements',
    group: 'grow',
  },
  {
    id: 'knowledge',
    label: 'Knowledge',
    description: 'Reference for growing the crop and getting the resin off it',
    group: 'learn',
  },
  {
    id: 'feed',
    label: 'Feed',
    description: 'Posts from growers you follow and the wider community',
    group: 'community',
  },
  {
    id: 'threads',
    label: 'Threads',
    description: 'Chronological grow stories with one audience setting',
    group: 'community',
  },
  {
    id: 'profile',
    label: 'Profile',
    description: 'Your opt-in social identity, followers, and shared posts',
    group: 'community',
  },
  {
    id: 'settings',
    label: 'Settings & Data',
    description: 'Profile defaults, bag strategies, backup, and exports',
    group: 'account',
  },
  {
    id: 'ui-lab',
    label: 'UI Lab',
    description: 'Shared component gallery for visual and interaction review',
    group: 'internal',
  },
] as const;

export type SectionId = (typeof SECTIONS)[number]['id'];

/**
 * Extraction and internal tooling remain deep-linkable without competing with
 * the platform's everyday grow, learning, and community destinations.
 */
export const PRIMARY_SECTIONS = SECTIONS.filter(
  (section) => section.group !== 'extract' && section.id !== 'ui-lab',
);

const GROUPS = [
  { id: 'home', label: 'Home' },
  { id: 'grow', label: 'Grow' },
  { id: 'extract', label: 'Extract' },
  { id: 'learn', label: 'Learn' },
  { id: 'community', label: 'Community' },
  { id: 'account', label: 'Account' },
] as const;

export const NAV_GROUPS = GROUPS.map((group) => ({
  ...group,
  sections: PRIMARY_SECTIONS.filter((section) => section.group === group.id),
})).filter((group) => group.sections.length > 0);

const COMPACT_NAV_SECTION_IDS = [
  'dashboard',
  'spaces',
  'feed',
  'knowledge',
] as const satisfies readonly SectionId[];

export const COMPACT_NAV_SECTIONS = COMPACT_NAV_SECTION_IDS.flatMap((id) => {
  const section = PRIMARY_SECTIONS.find((entry) => entry.id === id);
  return section ? [section] : [];
});

export const COMPACT_MORE_GROUPS = NAV_GROUPS.map((group) => ({
  ...group,
  sections: group.sections.filter(
    (section) =>
      !COMPACT_NAV_SECTION_IDS.some((compactId) => compactId === section.id),
  ),
})).filter((group) => group.sections.length > 0);

export const DEFAULT_SECTION: SectionId = 'dashboard';

export function isSectionId(value: string): value is SectionId {
  return SECTIONS.some((section) => section.id === value);
}

export function sectionLabel(id: SectionId): string {
  const match = SECTIONS.find((section) => section.id === id);
  return match ? match.label : id;
}
